import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { formatDate } from '@artosera/shared';
import { Button, Price } from '@artosera/ui';
import { appelDeRequete, cleMetier } from '../../api/cles.js';
import { presentationApi } from '../../api/presentation.js';
import { galerieALaction, galerieAModule } from '../../auth/sujet.js';
import { useEnLigne } from '../racine/BandeauHorsLigne.js';
import { useShellContext } from '../shell/context.js';
import styles from './Presentation.module.css';

interface SuggestionPrixApprouveProps {
  contactId: string | null;
  oeuvreId: string;
  exemplaireId?: string | null;
  devise?: string;
  onAppliquer: (prixCentimes: number) => void;
}

export function SuggestionPrixApprouve(props: SuggestionPrixApprouveProps) {
  const { galerie, galerieSlug, compte } = useShellContext();
  const enLigne = useEnLigne();
  if (
    !enLigne ||
    !props.contactId ||
    !galerieAModule(galerie, 'rendez-vous-client') ||
    !galerieALaction(galerie, 'presentation:read')
  )
    return null;
  return (
    <PrixApprouve
      key={`${compte.id}:${galerieSlug}:${props.contactId}:${props.oeuvreId}:${props.exemplaireId ?? ''}`}
      {...props}
      contactId={props.contactId}
    />
  );
}

function PrixApprouve({
  contactId,
  oeuvreId,
  exemplaireId = null,
  devise = 'EUR',
  onAppliquer,
}: SuggestionPrixApprouveProps & { contactId: string }) {
  const { galerieSlug } = useShellContext();
  const { t, i18n } = useTranslation('presentation');
  const langue = i18n.language === 'en' ? 'en' : 'fr';
  const [maintenant, setMaintenant] = useState(() => Date.now());
  const requete = useQuery({
    queryKey: cleMetier(galerieSlug, 'presentation', 'prix-approuve', contactId, oeuvreId),
    queryFn: async (contexte) => {
      const appel = appelDeRequete(contexte);
      const filtres = { valides: '1' as const, contactId, oeuvreId, parPage: 200 };
      const premiere = await presentationApi.approbations(galerieSlug, filtres, appel);
      const approbations = [...premiere.approbations];
      for (let page = 2; page <= premiere.pagination.pages; page++) {
        const suite = await presentationApi.approbations(galerieSlug, { ...filtres, page }, appel);
        approbations.push(...suite.approbations);
      }
      return approbations;
    },
  });
  const approbation = requete.data?.find(
    (accord) =>
      accord.contactId === contactId &&
      accord.oeuvreId === oeuvreId &&
      accord.exemplaireId === exemplaireId &&
      accord.statut === 'acceptee' &&
      accord.expireLe !== null &&
      Date.parse(accord.expireLe) > Math.max(maintenant, requete.dataUpdatedAt),
  );
  const expiration = approbation?.expireLe;
  useEffect(() => {
    if (!expiration) return;
    const minuterie = window.setTimeout(
      () => setMaintenant(Date.now()),
      Math.max(0, Date.parse(expiration) - Date.now()),
    );
    return () => window.clearTimeout(minuterie);
  }, [expiration]);
  if (!approbation || !expiration || requete.isError) return null;
  return (
    <div className={styles.ligne}>
      <span className={styles.aide}>
        {t('suggestionPrix.prix')}{' '}
        <Price cents={approbation.prixProposeCentimes} currency={devise} lang={langue} />{' '}
        {t('suggestionPrix.validite', {
          date: formatDate(new Date(expiration), langue),
        })}
      </span>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => {
          if (Date.now() < Date.parse(expiration)) onAppliquer(approbation.prixProposeCentimes);
          else setMaintenant(Date.now());
        }}
      >
        {t('suggestionPrix.appliquer')}
      </Button>
    </div>
  );
}
